import {
  Button,
  Card,
  CardBody,
  CardText,
  CardTitle,
  Col,
  Row,
} from 'reactstrap';
import { useContext, useEffect, useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import { CampaignContext } from '../../../context/campaign';
import {
  fetchCampaignMaterials,
  fetchMyCampaign,
} from '../../../services/campaign';
import {
  IMaterialsInfoTypes,
  MyCampaignResponse,
} from '../../../types/campaign';
import { CampaignHeaderSection } from '../../../shared/components/cards/CampaignItemsCard';
import { getDashColor, renderIcon } from '../../../shared/components/icons';
import { AdminContext } from '~/context/admin';

function HeaderCard() {
  return (
    <div>
      <h1 className='h2'>Helpful Tools</h1>
      <p>
        Asking for help can be hard. We make it easier. Find fundraising tools
        below, and contact your Client Services Coordinator for one-on-one
        fundraising help.
      </p>
    </div>
  );
}

interface HowToCardProps {
  title: string;
  body: string;
  href: string;
  href2?: string;
  linktext: string;
  linktext2?: string;
}

const HowToCard = ({
  title,
  body,
  href,
  href2 = '',
  linktext,
  linktext2 = '',
}: HowToCardProps) => {
  return (
    <Col>
      <Card
        style={{
          height: '100%',
          borderWidth: '0',
          background: getDashColor('supporters'),
        }}
      >
        <CardBody>
          <CardTitle style={{ fontWeight: '900' }}>{title}</CardTitle>
          <CardText>{body}</CardText>
          <a href={href} target='_blank'>
            {linktext}
          </a>
          {href2 && (
            <>
              <br />
              <a href={href2} target='_blank'>
                {linktext2}
              </a>
            </>
          )}
        </CardBody>
      </Card>
    </Col>
  );
};

export const HelpfulTools = ({}: any) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [{ campaign }, campaignDispatch] = useContext(CampaignContext);
  const [{ campaignId }, adminDispatch] = useContext(AdminContext);
  const [campaignMaterials, setCampaignMaterials] =
    useState<IMaterialsInfoTypes>();
  const history = useHistory();

  const getCampaignMaterials = async () => {
    try {
      const response = await fetchCampaignMaterials(campaignId);
      setCampaignMaterials(response);
      setLoading(false);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getCampaignMaterials();
  }, []);

  function HowToCards() {
    return (
      <Row className='row-cols-1 row-cols-md-2 row-cols-lg-4 g-4 mb-5'>
        <HowToCard
          title='Request Materials'
          body='Let us develop custom fundraising materials for your campaign:'
          href='https://s3.amazonaws.com/helphopelive/media/forms_n_flyers/HHL_Material_Request_Form_1KWcKZK.pdf'
          linktext='Request Form'
          href2='https://s3.amazonaws.com/helphopelive/media/forms_n_flyers/HHL_Sample_Flyers.pdf'
          linktext2='See Samples'
        />
        <HowToCard
          title='Fundraising Tips'
          body='Access our fundraising tips and how-to guides:'
          href='https://helphopelive.org/category/fundraising/'
          linktext='Tips & Guides'
        />
        <HowToCard
          title='Fundraiser Ideas'
          body='Ready to plan a fundraiser? Start with our ideas:'
          href='https://s3.amazonaws.com/helphopelive/media/forms_n_flyers/HHL_Fundraiser_Ideas_List_7VU9ogy.pdf'
          linktext='Fundraiser Ideas'
        />
        <HowToCard
          title='Fundraiser Checklist'
          body='Plan your next fundraiser with our checklist:'
          href='https://s3.amazonaws.com/helphopelive/media/forms_n_flyers/3_Planning_a_Fundraiser_Checklist.pdf'
          linktext='Checklist'
        />
      </Row>
    );
  }

  if (!campaign || loading) {
    return <div>Loading...</div>;
  }

  interface DocumentItem {
    url: string;
    name: string;
  }

  interface DocCardProps {
    title: string;
    docs: DocumentItem[];
  }

  const get_docs_links = (perms: DocumentItem[]) => {
    if (!perms) {
      return <></>;
    }
    return perms.map((item: DocumentItem, i: number) => {
      return (
        <span key={i}>
          <CardText>
            <a href={item.url} target='_blank'>
              {item.name}
            </a>
          </CardText>
        </span>
      );
    });
  };

  const DocCard = ({ title, docs }: DocCardProps) => {
    return (
      <Col>
        <Card
          style={{
            height: '100%',
            borderWidth: '1px',
          }}
        >
          <CardBody>
            <CardTitle>
              <h4>{title}</h4>
            </CardTitle>
            {get_docs_links(docs)}
          </CardBody>
        </Card>
      </Col>
    );
  };

  const { campaignDetail, campaignCoordinator } = campaign;

  return (
    <div className='container__inner'>
      <CampaignHeaderSection
        leftcard={<HeaderCard />}
        contact={campaignCoordinator}
      />
      <HowToCards />
      <Row className='row-cols-1 row-cols-md-2 row-cols-lg-3 g-4'>
        <DocCard title='Guidelines' docs={campaignMaterials?.resources || []} />
        <DocCard title='Forms' docs={campaignMaterials?.forms || []} />
        <DocCard title='Other Resources' docs={campaignMaterials?.tips || []} />
      </Row>
    </div>
  );
};
